import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { DateService } from '../../date/date.service';
import { DateParserService } from '../daily-log-time/date-parser-service';
import { OperationService } from '../../operations/operation/operation.service';
import { OperationValidatorService } from '../../operations/operation-validator.service';
import { MultipleDayService } from '../../mutiple-day/multiple-day.service';
import {
  DailyLogOperationInstance,
  DailyLogType,
  DailyOperationLogRecord,
  StepInstance,
  TypeSwitch
} from '../../../models/projects/operation.models';
import {
  ActionType,
  ContractualTimeGroup,
  ErrorType,
  HierarchyCollision,
  StartAndEnd,
  ValidationResult,
} from '../../../models/projects/validation.models';
import { PtLuxonDate } from '../../../models/projects/date.models';
// tslint:disable-next-line:max-line-length
import {
  ContractualOperationValidatorService
} from '../../operations/operation-validation/contractual-operation-validator-service/contractual-operation-validator.service';
import { cloneDeep } from 'lodash';
import { DelayService } from '../../delay/delay-service/delay.service';
import { DailyLogService } from '../daily-log.service';
import { AppDataSyncService } from '../../app-data-sync/app-data-sync.service';
import { ConsumablesService } from '../../consumables/consumables.service';
import { WeatherReadingsService } from '../../weather-readings/weather-readings.service';
import { VesselCrewService } from '../../vessel-crew/vessel-crew.service';
import { CompleteDayService } from '../../complete-day/complete-day.service';
import { TimeZoneService } from '../../time-zone/time-zone.service';
import { JackingActivityService } from '../../jacking-activity/jacking-activity.service';
import { OperationTimeService } from '../../operations/operation-time/operation-time.service';
import { SetupService } from '../../setup/setup.service';
import { AppConfig } from '../../../app/app.config';
import { DateTime } from 'luxon';
import { v4 as uuidv4 } from 'uuid';

@Injectable()
export class DailyLogValidationService {

  config = AppConfig.settings;

  constructor(
    private http: HttpClient,
    private dateService: DateService,
    private dateParserService: DateParserService,
    private operationService: OperationService,
    private operationValidatorService: OperationValidatorService,
    private contractualValidatorService: ContractualOperationValidatorService,
    private multipleDayService: MultipleDayService,
    private dailyLogService: DailyLogService,
    private delayService: DelayService,
    private vesselCrewService: VesselCrewService,
    private appDataSyncService: AppDataSyncService,
    private consumableService: ConsumablesService,
    private weatherReadingsService: WeatherReadingsService,
    private completeDayService: CompleteDayService,
    private timeZoneService: TimeZoneService,
    private jackingActivityService: JackingActivityService,
    private operationTimeService: OperationTimeService,
    private setupService: SetupService
  ) {

  }

  async validateInstances(
    instances: DailyLogOperationInstance[],
    dailyLogDate: PtLuxonDate,
    validationResult?: ValidationResult,
    records?: DailyOperationLogRecord[],
  ): Promise<ValidationResult> {
    if (!validationResult) {
      validationResult = new ValidationResult();
    }

    const startDate = cloneDeep(dailyLogDate.dateTime);
    const startDateTimeZone = await this.timeZoneService.getEditorLocally({
      dateTime: startDate,
      dateAmbiguous: true
    });

    if (!startDateTimeZone) {
      // no timezone implies user has gone back to a day before they started logging - so we don't care about validation
      return validationResult;
    }

    const endDate = await this.dateService.getEndOfDayLocally(startDate);

    validationResult = await this.handleGapValidation(dailyLogDate, startDate, endDate, validationResult, cloneDeep(instances));

    validationResult = await this.handleStepTimeValidation(cloneDeep(instances), validationResult);

    validationResult = await this.handleJackingOperationValidation(cloneDeep(instances), validationResult);

    validationResult = await this.handleOverlapValidation(dailyLogDate, startDate, endDate, cloneDeep(instances), validationResult);

    validationResult = await this.handleContractualHierarchyValidation(dailyLogDate, endDate, cloneDeep(instances), validationResult);

    validationResult = await this.handleOpenEndedOperationValidation(dailyLogDate, cloneDeep(instances), validationResult);

    validationResult = await this.handleUnconfirmedTimesValidation(dailyLogDate, records, validationResult);

    return validationResult;
  }

  private async handleGapValidation(
    dailyLogDate: PtLuxonDate,
    startDate: DateTime,
    endDate: PtLuxonDate,
    validationResult: ValidationResult,
    instances: DailyLogOperationInstance[]
  ): Promise<ValidationResult> {
    // if operation is concurrent but start and end aren't, it means it was switched using the contractual hierarchy auto-fix functionality
    const gaps = await this.findGapsInOperations(instances.filter(p =>
      !p.isConcurrentOperation && !p.isInterruption || (p.isConcurrentOperation && (p.switchTimes.some(sw => !sw.isConcurrent)))),
      dailyLogDate,
      endDate);
    const endDateCorrected = endDate.dateTime.minus({minute: 1});

    for (let i = 0; i < gaps.length; i++) {
      const gap = gaps[i];

      if (gap.operationInstance && gap.operationInstance.cancelledTime) {
        gap.operationInstance.endTime = new PtLuxonDate(gap.operationInstance.cancelledTime);
      }

      if (gap.previousOperation && gap.previousOperation.cancelledTime) {
        gap.previousOperation.endTime = new PtLuxonDate(gap.previousOperation.cancelledTime);
      }

      gap.type = ActionType.OperationGap;

      if (!gap.previousOperation && gap.operationInstance) {
        const operationName = gap.operationInstance.projectSpecificOperation.name
          || gap.operationInstance.projectSpecificOperation.operationSummary.name;
        const includeGapDate = this.showContractualDates(dailyLogDate.dateTime, [gap.startTime.dateTime, gap.endTime.dateTime]);
        const gapStart = this.dateService.setFullTimeString(gap.startTime.dateTime, includeGapDate);
        const gapEnd = this.dateService.setFullTimeString(gap.endTime.dateTime, includeGapDate);
        const errorMessage = `There is a gap during <strong>${operationName}</strong>, between <strong>${gapStart}</strong> and <strong>${gapEnd}</strong>`;
        validationResult.addError(errorMessage, ErrorType.Error, ActionType.OperationGap, gap);
      }

      const startsAtDayBoundary = !gap.previousOperation || gap.previousOperatioInstanceId === gap.operationInstanceId;
      const previousStart = startsAtDayBoundary ? startDate : gap.previousOperation.startTime.dateTime;
      const previousEnd = startsAtDayBoundary ? null : gap.previousOperation.endTime.dateTime;
      const nextStart = !gap.operationInstance ? endDateCorrected : gap.endTime.dateTime;
      const nextEnd = gap.operationInstance?.endTime?.dateTime;
      const includeDate = this.showContractualDates(dailyLogDate.dateTime, [previousStart, previousEnd, nextStart, nextEnd]);

      validationResult.addError(`There is a gap between <strong>
        ${(!gap.previousOperation || gap.previousOperatioInstanceId === gap.operationInstanceId)
          ? ' start of day '
          : gap.previousOperation.projectSpecificOperation.name || gap.previousOperation.projectSpecificOperation.operationSummary.name}
      ${this.dateService.setFullTimeString(previousStart, includeDate)}${(!gap.previousOperation || gap.previousOperatioInstanceId === gap.operationInstanceId)
        ? ''
        : ' to ' + this.dateService.setFullTimeString(previousEnd, includeDate)}
            </strong> and <strong> ${!gap.operationInstance
        ? ' End of day '
        : gap.operationInstance.projectSpecificOperation.name || gap.operationInstance.projectSpecificOperation.operationSummary.name}
              ${this.dateService.setFullTimeString(nextStart, includeDate)}${!gap.operationInstance
        ? ''
        : ' to ' + (gap.operationInstance.endTime && gap.operationInstance.endTime.dateTime
        ? this.dateService.setFullTimeString(nextEnd, includeDate)
        : ' End of day')} </strong>`,
        ErrorType.Error,
        ActionType.OperationGap,
        gap);
    }

    return validationResult;
  }

  private async handleStepTimeValidation(instances: DailyLogOperationInstance[], validationResult: ValidationResult): Promise<ValidationResult> {
    const setupData = await this.setupService.getSetupChangeLogData();

    for (const instance of instances) {
      let message = await this.operationValidatorService.checkOperationInAStage(instance, setupData);

      if (!!message) {
        message = !!instance.projectSpecificOperation.name
          ? instance.projectSpecificOperation.name + ': ' + message
          : instance.projectSpecificOperation.operationSummary.name + ': ' + message;
        validationResult.addError(message, ErrorType.Error);
      }

      const stepSpanErrors = this.operationValidatorService.checkStepSpans(instance);

      for (let i = 0; i < stepSpanErrors.length; i++) {
        const element = stepSpanErrors[i];
        validationResult.addError(`For record '${element.stepInstance.step.name}'
          your end time (${this.dateService.setTimeStringFromPtLuxonDate(element.stepInstance.endTime)})
          is equal to the record start time (${this.dateService.setTimeStringFromPtLuxonDate(element.stepInstance.startTime)}).
          This step has been assigned a duration by the Operations team, and is therefore not allowed to have
          a duration of 0 minutes.`, ErrorType.Error, ActionType.OperationRule);
      }

      validationResult = await this.validateStepTimes(validationResult, instance);
    }

    return validationResult;
  }

  private async handleJackingOperationValidation(instances: DailyLogOperationInstance[], validationResult: ValidationResult): Promise<ValidationResult> {
    const jackingGuids = this.jackingActivityService.getJackingActivityGuids();
    const arrivalInstances = instances.filter(operationInstance =>
      operationInstance.cancelledTime === null && // if op cancelled then don't show the warning
      operationInstance.projectSpecificOperation.operationSummary.guid.toLowerCase() ===
      jackingGuids.jackingArrivalOperationGuid.toLowerCase());

    // if there is no jack up port/offshore instance with the same jacking activity id, then add error and stop searching for more
    for (let i = 0; i < arrivalInstances.length; i++) {
      if (!instances.find(operationInstance2 =>
        (operationInstance2.projectSpecificOperation.operationSummary.guid.toLowerCase() ===
          jackingGuids.jackUpOffshoreOperationGuid.toLowerCase()
          || operationInstance2.projectSpecificOperation.operationSummary.guid.toLowerCase() ===
          jackingGuids.jackUpPortOperationGuid.toLowerCase()) && operationInstance2.jackingActivityId === arrivalInstances[i].jackingActivityId)) {
        validationResult.addError(`'Jack Up-Port' or 'Jack Up-Offshore' must be added.`, ErrorType.Warning, ActionType.OperationRule);
        break;
      }
    }

    return validationResult;
  }

  private async handleOverlapValidation(
    dailyLogDate: PtLuxonDate,
    startDate: DateTime,
    endDate: PtLuxonDate,
    instances: DailyLogOperationInstance[],
    validationResult: ValidationResult
  ): Promise<ValidationResult> {
    const overlaps = await this.findOverlapTimes(instances, dailyLogDate, endDate);

    if (!overlaps) {
      return validationResult;
    }

    for (const overlap of overlaps) {
      if (overlap.type !== ActionType.DelayPauseOverlap) {
        overlap.type = ActionType.OperationOverlap;
      }

      if (!!overlap.previousOperation) {
        overlap.previousOperation.startTimeToShow = this.dateService.setTimeStringFromPtLuxonDate(overlap.previousOperation.startTime);
        overlap.previousOperation.endTimeToShow = this.dateService.setTimeStringFromPtLuxonDate(overlap.previousOperation.endTime);
      }

      if (!!overlap.operationInstance) {
        overlap.operationInstance.startTimeToShow = this.dateService.setTimeStringFromPtLuxonDate(overlap.operationInstance.startTime);
        overlap.operationInstance.endTimeToShow = this.dateService.setTimeStringFromPtLuxonDate(overlap.operationInstance.endTime);
      }

      validationResult = await this.addOverlapValidationError(validationResult, overlap, startDate);
    }

    return validationResult;
  }

  private async handleContractualHierarchyValidation(
    dailyLogDate: PtLuxonDate,
    endDate: PtLuxonDate,
    instances: DailyLogOperationInstance[],
    validationResult: ValidationResult
  ): Promise<ValidationResult> {
    const collisions = await this.contractualHierarchy(instances, dailyLogDate, endDate);

    // filter delays from hierarchy collisions (this is also done in contractual-hierarchy.ts, after the call to contractual hierarchy)
    collisions.forEach(collision => {
      if (collision.higherInstance.projectSpecificOperation.operationSummary.classificationName === 'Delay'
        || collision.lowerInstance.projectSpecificOperation.operationSummary.classificationName === 'Delay') {
        return;
      }

      validationResult.addError(`There is a hierarchy collision between <strong>
        ${!!collision.higherInstance.projectSpecificOperation.name
        ? collision.higherInstance.projectSpecificOperation.name
        : collision.higherInstance.projectSpecificOperation.operationSummary.name}
        (${this.dateService.setFullTimeString(collision.higherInstance.startTime.dateTime)} -
         ${collision.higherInstance.endTime.dateTime ? this.dateService.setFullTimeString(collision.higherInstance.endTime.dateTime) : 'No End Time'},
         ${collision.higherInstance.projectSpecificOperation.contractualPriority
          ? 'priority ' + collision.higherInstance.projectSpecificOperation.contractualPriority
          : 'no priority'}) </strong>
         and <strong> ${!!collision.lowerInstance.projectSpecificOperation.name
        ? collision.lowerInstance.projectSpecificOperation.name
        : collision.lowerInstance.projectSpecificOperation.operationSummary.name}
         (${this.dateService.setFullTimeString(collision.lowerInstance.startTime.dateTime)} -
          ${collision.lowerInstance.endTime.dateTime ? this.dateService.setFullTimeString(collision.lowerInstance.endTime.dateTime) : 'No End Time'},
          ${collision.lowerInstance.projectSpecificOperation.contractualPriority
            ? 'priority ' + collision.lowerInstance.projectSpecificOperation.contractualPriority
            : 'no priority'}) </strong>`,
        ErrorType.Warning,
        ActionType.ContractualHierarchy);
    });

    return validationResult;
  }

  private async handleOpenEndedOperationValidation(
    dailyLogDate: PtLuxonDate,
    instances: DailyLogOperationInstance[],
    validationResult: ValidationResult
  ): Promise<ValidationResult> {
    const relevantInstances = await this.getInstancesRelevantToLogDay(instances, dailyLogDate);

    const openEndedCount = this.openEndedOperationCount(relevantInstances);

    openEndedCount.forEach(instance => {
      validationResult.addError(`Operation <strong>${instance.projectSpecificOperation.name
          ? instance.projectSpecificOperation.name
          : instance.projectSpecificOperation.operationSummary.name}</strong> has been left open ended`,
        ErrorType.Warning, ActionType.OpenEnded);
    });

    const multiDayCount = await this.nextDayOperationCount(relevantInstances, dailyLogDate);

    multiDayCount.forEach(instance => {
      validationResult.addError(`Operation <strong>${instance.projectSpecificOperation.name
          ? instance.projectSpecificOperation.name
          : instance.projectSpecificOperation.operationSummary.name}</strong> spans multiple days`,
        ErrorType.Warning,
        ActionType.Multiday);
    });

    return validationResult;
  }

  private async getInstancesRelevantToLogDay(
    instances: DailyLogOperationInstance[],
    dailyLogDate: PtLuxonDate
  ): Promise<DailyLogOperationInstance[]> {
    if (!instances?.length) {
      return [];
    }

    const endOfDay = await this.dateService.getEndOfDayLocally(dailyLogDate.dateTime);

    return instances.filter(instance => {
      const start = instance.startTime?.dateTime;
      const end = instance.cancelledTime || instance.endTime?.dateTime;

      if (!start) {
        return false;
      }

      // starts on the day
      if (start >= dailyLogDate.dateTime && start < endOfDay.dateTime) {
        return true;
      }

      // spans into / across the day
      if (end && start < endOfDay.dateTime && end > dailyLogDate.dateTime) {
        return true;
      }

      // open ended and started before end of this day
      if (!end && start < endOfDay.dateTime) {
        return true;
      }

      return false;
    });
  }

  private async handleUnconfirmedTimesValidation(
    dailyLogDate: PtLuxonDate,
    records: DailyOperationLogRecord[],
    validationResult: ValidationResult
  ): Promise<ValidationResult> {
    const foundUnconfirmed = await this.unconfirmedTimes(records, dailyLogDate);

    if (foundUnconfirmed) {
      validationResult.addError('You have unconfirmed times in your log', ErrorType.Error, ActionType.Unconfirmed);
    }

    return validationResult;
  }

  async validateStepTimes(validationResult: ValidationResult, instance: DailyLogOperationInstance): Promise<ValidationResult> {
    if (instance.stepCollectionInstance && instance.stepCollectionInstance.stepInstances) {
      for (let i = 0; i < instance.stepCollectionInstance.stepInstances.length; i++) {
        if (this.stepOutsideOperationTimes(instance.stepCollectionInstance.stepInstances[i], instance)) {
          validationResult.addError(`Step '${instance.stepCollectionInstance.stepInstances[i].step.name}',
          in Operation '${instance.projectSpecificOperation.name || instance.projectSpecificOperation.operationSummary.name}',
          falls outside of the Operation Start and End times.`, ErrorType.Error, ActionType.OperationRule);
        }
      }
    }

    return validationResult;
  }

  stepOutsideOperationTimes(stepInstance: StepInstance, operationInstance: DailyLogOperationInstance): boolean {
    if (stepInstance.exclude || !operationInstance.endTime || !operationInstance.endTime.dateTime) {
      return false;
    }

    // we checked operation open ended above so if there's none of these then we know it's outside the boundary
    if (!stepInstance.startTime || !stepInstance.startTime.dateTime || !stepInstance.endTime || !stepInstance.endTime.dateTime) {
      return true;
    }

    if (stepInstance.startTime.dateTime < operationInstance.startTime.dateTime || stepInstance.startTime.dateTime > operationInstance.endTime.dateTime) {
      if (operationInstance.cancelledTime && stepInstance.startTime.dateTime > operationInstance.cancelledTime) {
        return false;
      }
      return true;
    }

    if (stepInstance.endTime.dateTime < operationInstance.startTime.dateTime || stepInstance.endTime.dateTime > operationInstance.endTime.dateTime) {
      if (operationInstance.cancelledTime && stepInstance.endTime.dateTime > operationInstance.cancelledTime) {
        return false;
      }
      return true;
    }

    return false;
  }

  async findGapsInOperations(instances: DailyLogOperationInstance[], dailyLogDate?: PtLuxonDate, endDay?: PtLuxonDate): Promise<StartAndEnd[]> {
    if (!dailyLogDate) {
      dailyLogDate = await this.dailyLogService.getCurrentLogDay();
    }

    if (!endDay) {
      endDay = await this.dateService.getEndOfDayLocally(dailyLogDate.dateTime);
    }

    if (!instances || instances.length < 1 && dailyLogDate.dateTime) {
      const startDay = new PtLuxonDate(dailyLogDate.dateTime);

      const wholeDay = new StartAndEnd();
      wholeDay.startTime = startDay;
      wholeDay.endTime = endDay;
      wholeDay.type = ActionType.OperationGap;
      return [wholeDay];
    }

    const allTimeGroups = [] as ContractualTimeGroup[];
    const concurrentTimeGroups = [] as ContractualTimeGroup[];
    const contractualTimeGroups = [] as ContractualTimeGroup[];

    for (let i = 0; i < instances.length; i++) {
      // if a op doesn't have an end time, then set the end of day as it's end time
      // unless op has a cancel time - then that is the end time instead
      // date passed in here should be local start of day, and cancelled time if it exists should be UTC
      if (dailyLogDate.dateTime && !instances[i].endTime.getDate()) {
        if (instances[i].cancelledTime) {
          instances[i].endTime = new PtLuxonDate(instances[i].cancelledTime);
        } else {
          instances[i].endTime = endDay;
        }
      }
    }

    for (let i = 0; i < instances.length; i++) {
      const contractualTimes = await this.contractualValidatorService.getTimesForOperation(instances[i], false, endDay, dailyLogDate);
      const concurrentTimes = await this.contractualValidatorService.getTimesForOperation(instances[i], true, endDay, dailyLogDate);
      allTimeGroups.push(contractualTimes);
      allTimeGroups.push(concurrentTimes);
      contractualTimeGroups.push(contractualTimes);
      concurrentTimeGroups.push(concurrentTimes);
    }

    const gaps = await this.findGapsTimes(contractualTimeGroups, dailyLogDate);

    return gaps;
  }

  async findGapsTimes(contractualTimes: ContractualTimeGroup[], dailyLogDate?: PtLuxonDate): Promise<StartAndEnd[]> {
    const gaps = [] as StartAndEnd[];

    if (!contractualTimes) {
      return gaps;
    }

    const allContractualTimesGroup = new ContractualTimeGroup();

    contractualTimes.forEach(time => {
      allContractualTimesGroup.times = allContractualTimesGroup.times.concat(time.times);
    });

    allContractualTimesGroup.isContractual = true;

    let latestPtDate = cloneDeep(dailyLogDate) as PtLuxonDate;
    let latestOperation = null as DailyLogOperationInstance;
    const endDay = await this.dateService.getEndOfDayLocally(latestPtDate.dateTime);
    endDay.dateTime = endDay.dateTime.minus({minute: 1});

    let latestId = null;
    let found = true;
    let first = true;

    allContractualTimesGroup.times = allContractualTimesGroup.times
      .filter(p => p.operationInstance != null);

    allContractualTimesGroup.times
      .sort((a, b) => a.operationInstance.startTime.dateTime.toMillis() - b.operationInstance.startTime.dateTime.toMillis());

    let laterTimes = allContractualTimesGroup.times;
    let limit = 100;
    let firstRunOver = false;

    while (laterTimes.length > 0) {
      limit--;

      if (limit < 0) {
        limit = 100;
        break;
      }

      if (firstRunOver) {
        latestPtDate = cloneDeep(laterTimes[0].endTime);
      }

      latestOperation = cloneDeep(laterTimes[0].operationInstance);
      latestId = cloneDeep(laterTimes[0].operationInstanceId);

      let innerLimit = 100;

      while (found === true) {
        innerLimit--;

        if (innerLimit < 0) {
          innerLimit = 100;
          break;
        }

        for (const time of allContractualTimesGroup.times) {
          // checks if latestDate is inside the time.startTime and time.endTime
          const latestPtLuxonDate = time.endTime != null && time.endTime.dateTime > time.startTime.dateTime
            ? time.endTime
            : time.startTime;
          found = this.operationService.operationInTime(
            time.startTime.dateTime,
            time.endTime.dateTime,
            latestPtDate.dateTime,
            latestPtDate.dateTime) && latestPtDate.dateTime < latestPtLuxonDate.dateTime;

          if (found) {
            // if latestPtDate is encompassed by time.start/end, and is smaller than end, then set latest to be the time.end and loop again
            latestPtDate = cloneDeep(latestPtLuxonDate);
            latestOperation = cloneDeep(time.operationInstance);
            latestId = cloneDeep(time.operationInstanceId);
            break;
          }
        }
      }

      laterTimes = laterTimes
        .filter(p => (p.startTime.dateTime < p.endTime.dateTime ? p.startTime.dateTime : p.endTime.dateTime) > latestPtDate.dateTime)
        .sort((a, b) => a.operationInstance.startTime.dateTime.toMillis() - b.operationInstance.startTime.dateTime.toMillis());

      if (!found) {
        first = false;
        const gap = new StartAndEnd();
        gap.startTime = cloneDeep(latestPtDate);

        if (laterTimes.length > 0) {
          gap.endTime = cloneDeep(laterTimes[0].startTime);
          gap.operationInstance = cloneDeep(laterTimes[0].operationInstance);
          gap.operationInstanceId = cloneDeep(laterTimes[0].operationInstanceId);
        } else {
          gap.endTime = cloneDeep(endDay);
        }

        gap.previousOperation = latestOperation;
        gap.previousOperatioInstanceId = latestId;

        if ((firstRunOver === false && !!gap.operationInstance
          && (!gap.previousOperation || gap.previousOperation.endTimeToShow === '00:00'))
          || gap.previousOperatioInstanceId === gap.operationInstanceId) {
          gap.previousOperatioInstanceId = null;
          gap.previousOperation = null;
        }

        if (endDay.dateTime > latestPtDate.dateTime) {
          gaps.push(gap);
        }
      }

      found = true;
      firstRunOver = true;
    }

    return gaps;
  }

  async findOverlapTimes(instances: DailyLogOperationInstance[], dailyLogDate: PtLuxonDate, endDate?: PtLuxonDate): Promise<StartAndEnd[]> {
    const instancesFiltered = instances
      .filter(e => (!e.isConcurrentOperation && !e.isInterruption) || (e.isConcurrentOperation && e.switchTimes.some(s => !s.isConcurrent)));
    const contractualTimesUnfiltered: ContractualTimeGroup[] = [];
    const contractualTimes: ContractualTimeGroup[] = [];

    for (let i = 0; i < instancesFiltered.length; i++) {
      const contractualTimesTemp = await this.contractualValidatorService.getTimesForOperation(instancesFiltered[i], false, null, dailyLogDate);
      contractualTimesUnfiltered.push(contractualTimesTemp);
    }

    if (!contractualTimesUnfiltered) {
      return [];
    }

    for (let i = 0; i < contractualTimesUnfiltered.length; i++) {
      if (contractualTimesUnfiltered[i].times && contractualTimesUnfiltered[i].times[0]) {
        contractualTimesUnfiltered[i].number = i;
        contractualTimes.push(contractualTimesUnfiltered[i]);
      }
    }

    let allOverlaps: StartAndEnd[] = [];

    // loop through contractual times, at each loop, check each time against each other time
    for (let i = 0; i < contractualTimes.length; i++) {
      for (let j = 0; j < contractualTimes.length; j++) {
        // if comparing two of the same instance
        if (contractualTimes[j].number === contractualTimes[i].number) {
          continue;
        }

        // if the two operations being compared have already been checked and an overlap was found, continue so no duplicate error
        if (allOverlaps.some(e => {
          if (
            ((contractualTimes[i].operationInstance.id
              ? e.previousOperation.id === contractualTimes[i].operationInstance.id
              : e.previousOperation.localId === contractualTimes[i].operationInstance.localId)
              && (contractualTimes[j].operationInstance.id
                ? e.operationInstance.id === contractualTimes[j].operationInstance.id
                : e.operationInstance.localId === contractualTimes[j].operationInstance.localId))
            || ((contractualTimes[j].operationInstance.id
            ? e.previousOperation.id === contractualTimes[j].operationInstance.id
            : e.previousOperation.localId === contractualTimes[j].operationInstance.localId)
            && (contractualTimes[i].operationInstance.id
              ? e.operationInstance.id === contractualTimes[i].operationInstance.id
              : e.operationInstance.localId === contractualTimes[i].operationInstance.localId))
          ) {
            return true;
          }
        })) {
          continue;
        }

        // set end times to the cancelled time if there is one
        if (contractualTimes[i].operationInstance.cancelledTime) {
          contractualTimes[i].operationInstance.endTime = new PtLuxonDate(contractualTimes[i].operationInstance.cancelledTime);
        }

        if (contractualTimes[j].operationInstance.cancelledTime) {
          contractualTimes[j].operationInstance.endTime = new PtLuxonDate(contractualTimes[j].operationInstance.cancelledTime);
        }

        for (let k = 0; k < contractualTimes[i].times.length; k++) {
          for (let l = 0; l < contractualTimes[j].times.length; l++) {
            let overlapping = this.operationService.operationInTimeExclusive(
              contractualTimes[j].times[l].startTime.dateTime,
              contractualTimes[j].times[l].endTime.dateTime,
              contractualTimes[i].times[k].startTime.dateTime,
              contractualTimes[i].times[k].endTime.getDate());

            if (!overlapping) {
              overlapping = this.operationService.operationInTimeExclusive(
                contractualTimes[i].times[k].startTime.dateTime,
                contractualTimes[i].times[k].endTime.dateTime,
                contractualTimes[j].times[l].startTime.dateTime,
                contractualTimes[j].times[l].endTime.getDate());
            }

            if (!overlapping) {
              let startMatch = false;

              if (!contractualTimes[i].times[k].startTime.dateTime || !contractualTimes[j].times[l].startTime.getDate()) {
                startMatch = contractualTimes[i].times[k].endTime.dateTime === contractualTimes[j].times[l].startTime.dateTime;
              } else {
                startMatch = Math.abs(contractualTimes[i].times[k].startTime.dateTime.toMillis() - contractualTimes[j].times[l].startTime.dateTime.toMillis()) < 1000;
              }

              let endMatch = false;

              if (!contractualTimes[i].times[k].endTime.dateTime || !contractualTimes[j].times[l].endTime.getDate()) {
                endMatch = contractualTimes[i].times[k].endTime.dateTime === contractualTimes[j].times[l].endTime.dateTime;
              } else {
                endMatch = Math.abs(contractualTimes[i].times[k].endTime.dateTime.toMillis() - contractualTimes[j].times[l].endTime.dateTime.toMillis()) < 1000;
              }

              overlapping = startMatch && endMatch;
            }

            if (overlapping) {
              const overlapTime = new StartAndEnd();
              overlapTime.startTime = cloneDeep(contractualTimes[i].times[k].startTime);
              overlapTime.endTime = cloneDeep(contractualTimes[i].times[k].endTime);
              overlapTime.operationInstance = cloneDeep(contractualTimes[i].times[k].operationInstance);
              overlapTime.operationInstanceId = cloneDeep(contractualTimes[i].times[k].operationInstance.id
                ? contractualTimes[i].times[k].operationInstance.id
                : contractualTimes[i].times[k].operationInstance.localId);
              overlapTime.previousOperation = cloneDeep(contractualTimes[j].times[l].operationInstance);
              overlapTime.previousOperatioInstanceId = cloneDeep(contractualTimes[j].times[l].operationInstance.id
                ? contractualTimes[j].times[l].operationInstance.id
                : contractualTimes[j].times[l].operationInstance.localId);

              if ((overlapTime.operationInstance.delayOperationApiIds && overlapTime.operationInstance.delayOperationApiIds
                  .some(o => o === overlapTime.previousOperatioInstanceId))
                || (overlapTime.operationInstance.delayOperationLocalIds && overlapTime.operationInstance.delayOperationLocalIds
                  .some(o => o === overlapTime.previousOperation.localId))
                || (overlapTime.previousOperation.delayOperationApiIds && overlapTime.previousOperation.delayOperationApiIds
                  .some(o => o === overlapTime.operationInstanceId))
                || (overlapTime.previousOperation.delayOperationLocalIds && overlapTime.previousOperation.delayOperationLocalIds
                  .some(o => o === overlapTime.operationInstance.localId))) {
                continue;
              }

              allOverlaps.push(overlapTime);
            }
          }
        }
      }
    }

    allOverlaps = allOverlaps.concat(await this.checkDelayOverlaps(instancesFiltered, dailyLogDate, endDate));

    return allOverlaps;
  }

  async checkDelayOverlaps(instances: DailyLogOperationInstance[], dailyLogDate: PtLuxonDate, endOfDay?: PtLuxonDate): Promise<StartAndEnd[]> {
    const delayOverlaps = [] as StartAndEnd[];
    const delayInstances = instances.filter(e => e.projectSpecificOperation.operationSummary.classificationName === 'Delay');

    if (!endOfDay) {
      endOfDay = await this.dateService.getEndOfDayLocally(dailyLogDate.dateTime);
    }

    for (let i = 0; i < instances.length; i++) {
      // step overlaps with delays // op end and op start overlaps are below
      if (instances[i].pausedTimes) {
        for (let j = 0; j < instances[i].pausedTimes.length; j++) {
          instances[i].pausedTimes[j].startDate = new PtLuxonDate(instances[i].pausedTimes[j].startDate.dateTime);
          instances[i].pausedTimes[j].endDate = new PtLuxonDate(instances[i].pausedTimes[j].endDate.dateTime);

          const overlappingStart = instances[i].startTime && instances[i].startTime.dateTime &&
            instances[i].startTime.dateTime >= instances[i].pausedTimes[j].startDate.dateTime &&
            (instances[i].startTime.dateTime < instances[i].pausedTimes[j].endDate.dateTime || !instances[i].pausedTimes[j].endDate.dateTime);
          // if instance is open ended, it would have been set to end of day earlier, so to check for open ended at this stage we need to compare to endOfDay
          const overlappingEnd = instances[i].endTime && instances[i].endTime.dateTime && instances[i].endTime.dateTime.toMillis() !== endOfDay.dateTime.toMillis() &&
            instances[i].endTime.dateTime > instances[i].pausedTimes[j].startDate.dateTime &&
            (instances[i].endTime.dateTime < instances[i].pausedTimes[j].endDate.dateTime || !instances[i].pausedTimes[j].endDate.dateTime);
          const overlappingSteps = instances[i].stepCollectionInstance && instances[i].stepCollectionInstance.stepInstances &&
            instances[i].stepCollectionInstance.stepInstances.filter(e => !e.exclude &&
              ((e.startTime && e.startTime.dateTime && e.startTime.dateTime <= instances[i].pausedTimes[j].startDate.dateTime &&
                e.endTime && e.endTime.dateTime > instances[i].pausedTimes[j].startDate.dateTime) ||
              (e.startTime && e.startTime.dateTime && e.startTime.dateTime >= instances[i].pausedTimes[j].startDate.dateTime &&
                (!instances[i].pausedTimes[j].endDate.dateTime || e.startTime.dateTime < instances[i].pausedTimes[j].endDate.dateTime))));

          // if a step starts before the pause start and finishes after the pause start, or a step starts inside the pause
          if (overlappingStart) {
            const overlapTime = new StartAndEnd();
            overlapTime.type = ActionType.DelayPauseOverlap;
            overlapTime.startTime = cloneDeep(instances[i].startTime);
            overlapTime.endTime = cloneDeep(instances[i].endTime);
            overlapTime.operationInstance = cloneDeep(instances[i]);
            overlapTime.operationInstanceId = cloneDeep(instances[i].id
              ? instances[i].id
              : instances[i].localId);
            overlapTime.previousOperation = cloneDeep(delayInstances.find(e => e.id === instances[i].pausedTimes[j].delayApiId));
            overlapTime.previousOperatioInstanceId = cloneDeep(instances[i].pausedTimes[j].delayApiId);
            overlapTime.operationStartOnlyOverlapTime = instances[i].startTime;

            // if overlap is on the next day then ignore it
            if (overlapTime.startTime.dateTime >= endOfDay.dateTime) {
              continue;
            }

            delayOverlaps.push(overlapTime);
          }

          if (overlappingEnd) {
            const overlapTime = new StartAndEnd();
            overlapTime.type = ActionType.DelayPauseOverlap;
            overlapTime.startTime = cloneDeep(instances[i].startTime);
            overlapTime.endTime = cloneDeep(instances[i].endTime);
            overlapTime.operationInstance = cloneDeep(instances[i]);
            overlapTime.operationInstanceId = cloneDeep(instances[i].id
              ? instances[i].id
              : instances[i].localId);
            overlapTime.previousOperation = cloneDeep(delayInstances.find(e => e.id === instances[i].pausedTimes[j].delayApiId));
            overlapTime.previousOperatioInstanceId = cloneDeep(instances[i].pausedTimes[j].delayApiId);
            overlapTime.operationEndOnlyOverlapTime = instances[i].endTime;

            // if overlap is on the next day then ignore it
            if (overlapTime.startTime.dateTime >= endOfDay.dateTime) {
              continue;
            }

            delayOverlaps.push(overlapTime);
          }

          // if a step starts before the pause start and finishes after the pause start, or a step starts inside the pause
          if (overlappingSteps && overlappingSteps.length > 0) {
            overlappingSteps.forEach(s => {
              const overlapTime = new StartAndEnd();
              overlapTime.stepName = s.step.name;
              overlapTime.stepInstance = cloneDeep(s);
              overlapTime.type = ActionType.DelayPauseOverlap;
              overlapTime.startTime = cloneDeep(s.startTime);
              overlapTime.endTime = cloneDeep(s.endTime);
              overlapTime.operationInstance = cloneDeep(instances[i]);
              overlapTime.operationInstanceId = cloneDeep(instances[i].id
                ? instances[i].id
                : instances[i].localId);
              overlapTime.previousOperation = cloneDeep(delayInstances.find(e => e.id === instances[i].pausedTimes[j].delayApiId));
              overlapTime.previousOperatioInstanceId = cloneDeep(instances[i].pausedTimes[j].delayApiId);

              // only add it if it's on todays log date
              if (overlapTime.startTime.dateTime < endOfDay.dateTime) {
                delayOverlaps.push(overlapTime);
              }
            });
          }
        }
      }
    }

    return delayOverlaps;
  }

  async unconfirmedTimes(records: DailyOperationLogRecord[], dailyLogDate: PtLuxonDate): Promise<boolean> {
    if (!records) {
      return true;
    }

    let unconfirmedFound = false;

    const stepRecords = records.filter(p => p.typeId === DailyLogType.Step);
    const startRecords = records.filter(p => p.typeId === DailyLogType.OperationStart);
    const endRecords = records.filter(p => p.typeId === DailyLogType.OperationEnd);

    for (const element of stepRecords) {
      if (!element.archived && !element.startTimeConfirmed && element.startTime
        && await this.dateService.compareDayOffsetsPreApplied(element.startTime.dateTime, dailyLogDate.dateTime)) {
        unconfirmedFound = true;
      }

      if (element.startTimeShow === element.endTimeShow) {
        continue;
      }

      if (!element.archived && !element.endTimeConfirmed
        && await this.dateService.compareDayOffsetsPreApplied(element.endTime.dateTime, dailyLogDate.dateTime)) {
        unconfirmedFound = true;
      }
    }

    for (const element of startRecords) {
      if (!element.archived && !element.startTimeConfirmed
        && await this.dateService.compareDayOffsetsPreApplied(element.startTime.dateTime, dailyLogDate.dateTime)) {
        unconfirmedFound = true;
      }
    }

    for (const element of endRecords) {
      if (!element.archived && !element.endTimeConfirmed
        && await this.dateService.compareDayOffsetsPreApplied(element.endTime.dateTime, dailyLogDate.dateTime)) {
        unconfirmedFound = true;
      }
    }

    return unconfirmedFound;
  }

  async unconfirmedInstanceTimes(oldInstances: DailyLogOperationInstance[]): Promise<boolean> {
    if (!oldInstances || oldInstances.length < 1) {
      return false;
    }

    let instances = await this.operationService.getProjectSpecificOperationInstances(
      oldInstances
        .sort((a, b) => a.startTime.dateTime.toMillis() - b.startTime.dateTime.toMillis())
        .map(p => p.id)
    );


    if (!instances[0]) {
      instances = await this.operationService.getProjectSpecificOperationInstances(
        oldInstances
          .sort((a, b) => a.startTime.dateTime.toMillis() - b.startTime.dateTime.toMillis())
          .map(p => p.localId)
      );

    }

    let unconfirmedFound = false;

    const latestDay = await instances[0].startTime.getDay();

    for (const element of instances) {
      if (await element.startTime.getDay() > latestDay) {
        return;
      }

      if (!element.startConfirmed) {
        unconfirmedFound = true;
      }

      const steps = !element.stepCollectionInstance ? [] : element.stepCollectionInstance.stepInstances;

      for (const step of steps) {
        if (await step.startTime.getDay() > latestDay) {
          return;
        }

        if (!step.startConfirmed) {
          unconfirmedFound = true;
        }

        if (await step.endTime.getDay() > latestDay) {
          return;
        }

        if (!step.endConfirmed && step.secondsSpan > 500) {
          unconfirmedFound = true;
        }
      }

      if (await element.endTime.getDay() > latestDay) {
        return;
      }

      if (!element.endConfirmed) {
        unconfirmedFound = true;
      }

    }

    return unconfirmedFound;
  }

  async contractualHierarchy(
    instances: DailyLogOperationInstance[],
    dailyLogDate: PtLuxonDate,
    endOfDay?: PtLuxonDate,
  ): Promise<HierarchyCollision[]> {
    const allCollisions = [] as HierarchyCollision[];

    for (const instance of instances) {
      const collisions = await this.operationValidatorService.detectHierarchyMisplacements(instance, instances, dailyLogDate, endOfDay);

      for (const collision of collisions) {
        const matchingCollision = allCollisions.some(p =>
          p.higherInstanceTempId === collision.higherInstanceTempId &&
          p.lowerInstanceTempId === collision.lowerInstanceTempId);

        if (!matchingCollision) {
          collision.higherInstance.startTimeToShow = this.dateService.setTimeStringFromPtLuxonDate(collision.higherInstance.startTime);

          if (collision.higherInstance.cancelledTime) {
            const cancelledTime = new PtLuxonDate(collision.higherInstance.cancelledTime);
            collision.higherInstance.endTimeToShow = this.dateService.setTimeStringFromPtLuxonDate(cancelledTime);
          } else {
            collision.higherInstance.endTimeToShow = (this.dateService.setTimeStringFromPtLuxonDate(collision.higherInstance.endTime) || '(No End Time)');
          }

          collision.lowerInstance.startTimeToShow = this.dateService.setTimeStringFromPtLuxonDate(collision.lowerInstance.startTime);

          if (collision.lowerInstance.cancelledTime) {
            const cancelledTime = new PtLuxonDate(collision.lowerInstance.cancelledTime);
            collision.lowerInstance.endTimeToShow = this.dateService.setTimeStringFromPtLuxonDate(cancelledTime);
          } else {
            collision.lowerInstance.endTimeToShow = (this.dateService.setTimeStringFromPtLuxonDate(collision.lowerInstance.endTime) || '(No End Time)');
          }

          allCollisions.push(collision);
        }
      }
    }

    return allCollisions;
  }

  async resolveContractualHierarchyCollision(
    oldCollision: HierarchyCollision,
    newCollision?: boolean): Promise<DailyLogOperationInstance[]> {

    let collision = new HierarchyCollision();

    if (newCollision) {
      collision = cloneDeep(oldCollision);
    } else {
      collision = oldCollision;
    }

    const collisionStart = collision.collisionStart;
    const collisionEnd = collision.collisionEnd;
    const lowerInstance = await this.operationService.getProjectSpecificOperationInstance(collision.lowerInstanceTempId);
    const higherInstance = await this.operationService.getProjectSpecificOperationInstance(collision.higherInstanceTempId);

    // we don't save operations end times with the cancelled time, but setting the end time to be equal to the cancel time if it exists here
    // is a quick little workaround for a bug where the resolve for hierarchy validation would incorrectly 'resolve' an overlap even after the operation was cancelled
    if (lowerInstance.cancelledTime) {
      lowerInstance.endTime = new PtLuxonDate(lowerInstance.cancelledTime);
    }

    if (higherInstance.cancelledTime) {
      higherInstance.endTime = new PtLuxonDate(higherInstance.cancelledTime);
    }

    if (Math.abs(collisionStart.dateTime.toMillis() - lowerInstance.startTime.dateTime.toMillis()) < 1000) {
      lowerInstance.isConcurrentOperation = !lowerInstance.isConcurrentOperation;
    } else {
      const lowerFirstSwitch = new TypeSwitch();
      lowerFirstSwitch.id = this.delayService.calculateSwitchId(lowerInstance.switchTimes);
      lowerFirstSwitch.isConcurrent = !this.operationService.operationConcurrentAtTime(lowerInstance, collisionStart.getDate());
      lowerFirstSwitch.date = collisionStart;
      lowerFirstSwitch.guid = uuidv4();

      // don't add switch if it has the same start time as the instance end (unless end is null)
      if ((!lowerInstance.endTime || !lowerInstance.endTime.dateTime)
        || (lowerInstance.endTime && lowerInstance.endTime.dateTime && lowerFirstSwitch.date.dateTime.toMillis() < lowerInstance.endTime.dateTime.toMillis())
        || lowerInstance.switchTimes.some(s => s.date.dateTime === lowerFirstSwitch.date.dateTime)) {
        lowerFirstSwitch.dateToShow = this.dateService.setTimeStringFromPtLuxonDate(lowerFirstSwitch.date);
        lowerInstance.switchTimes.push(lowerFirstSwitch);
      }
    }

    const lowerSecondSwitch = new TypeSwitch();
    lowerSecondSwitch.id = this.delayService.calculateSwitchId(lowerInstance.switchTimes);
    lowerSecondSwitch.isConcurrent = !this.operationService.operationConcurrentAtTime(lowerInstance, collisionEnd.getDate());
    lowerSecondSwitch.date = collisionEnd;
    lowerSecondSwitch.guid = uuidv4();

    // don't add switch if it has the same start time as the instance end (unless end is null)
    if ((!lowerInstance.endTime || !lowerInstance.endTime.dateTime)
      || (lowerInstance.endTime && lowerInstance.endTime.dateTime && lowerSecondSwitch.date.dateTime.toMillis() < lowerInstance.endTime.dateTime.toMillis())
      || lowerInstance.switchTimes.some(s => s.date.dateTime === lowerSecondSwitch.date.dateTime)) {
      lowerSecondSwitch.dateToShow = this.dateService.setTimeStringFromPtLuxonDate(lowerSecondSwitch.date);
      lowerInstance.switchTimes.push(lowerSecondSwitch);
    }

    if (Math.abs(collisionStart.dateTime.toMillis() - higherInstance.startTime.dateTime.toMillis()) < 1000) {
      higherInstance.isConcurrentOperation = !higherInstance.isConcurrentOperation;
    } else {
      const higherFirstSwitch = new TypeSwitch();
      higherFirstSwitch.id = this.delayService.calculateSwitchId(higherInstance.switchTimes);
      higherFirstSwitch.isConcurrent = !this.operationService.operationConcurrentAtTime(higherInstance, collisionStart.getDate());
      higherFirstSwitch.date = collisionStart;
      higherFirstSwitch.guid = uuidv4();

      // don't add switch if it has the same start time as the instance end (unless end is null)
      if ((!higherInstance.endTime || !higherInstance.endTime.dateTime)
        || (higherInstance.endTime && higherInstance.endTime.dateTime && higherFirstSwitch.date.dateTime.toMillis() < higherInstance.endTime.dateTime.toMillis())
        || higherInstance.switchTimes.some(s => s.date.dateTime === higherFirstSwitch.date.dateTime)) {
        higherFirstSwitch.dateToShow = this.dateService.setTimeStringFromPtLuxonDate(higherFirstSwitch.date);
        higherInstance.switchTimes.push(higherFirstSwitch);
      }
    }

    const higherSecondSwitch = new TypeSwitch();
    higherSecondSwitch.id = this.delayService.calculateSwitchId(higherInstance.switchTimes);
    higherSecondSwitch.isConcurrent = !this.operationService.operationConcurrentAtTime(higherInstance, collisionEnd.getDate());
    higherSecondSwitch.date = collisionEnd;
    higherSecondSwitch.guid = uuidv4();

    // don't add switch if it has the same start time as the instance end (unless end is null)
    if ((!higherInstance.endTime || !higherInstance.endTime.dateTime)
      || (higherInstance.endTime && higherInstance.endTime.dateTime && higherSecondSwitch.date.dateTime.toMillis() < higherInstance.endTime.dateTime.toMillis())
      || higherInstance.switchTimes.some(s => s.date.dateTime === higherSecondSwitch.date.dateTime)) {
      higherSecondSwitch.dateToShow = this.dateService.setTimeStringFromPtLuxonDate(higherSecondSwitch.date);
      higherInstance.switchTimes.push(higherSecondSwitch);
    }

    const instances = [] as DailyLogOperationInstance[];
    instances.push(higherInstance);
    instances.push(lowerInstance);
    return instances;
  }

  openEndedOperationCount(instances: DailyLogOperationInstance[]): DailyLogOperationInstance[] {
    if (!instances) {
      return [];
    }

    instances.forEach(instance => {
      instance.endTime = PtLuxonDate.assignPtLuxonDate(instance.endTime);
      instance.startTime = PtLuxonDate.assignPtLuxonDate(instance.startTime);
    });

    return instances
      .filter(p => (p.endTime.dateTime == null || isNaN(p.endTime.dateTime.toMillis())) && p.cancelledTime == null);
  }

  async nextDayOperationCount(instances: DailyLogOperationInstance[], dailyLogDate?: PtLuxonDate): Promise<any> {
    if (!instances) {
      return [];
    }

    let ptDate = cloneDeep(dailyLogDate);

    if (!ptDate) {
      ptDate = await this.dailyLogService.getCurrentLogDay();
    }

    const endOfDay = await this.dateService.getEndOfDayLocally(ptDate.dateTime);

    return instances.filter(instance => this.operationStartsOnPreviousDay(ptDate, instance) || this.operationEndsOnFutureDay(endOfDay, instance));
  }

  private operationEndsOnFutureDay(endOfDay: PtLuxonDate, instance: DailyLogOperationInstance) {
    if (instance.cancelledTime && instance.cancelledTime < endOfDay.dateTime) {
      return false;
    }

    return instance.endTime === null || instance.endTime.dateTime === null || instance.endTime.dateTime > endOfDay.dateTime;
  }

  private operationStartsOnPreviousDay(dailyLogDate: PtLuxonDate, instance: DailyLogOperationInstance) {
    return instance.startTime.dateTime < dailyLogDate.dateTime;
  }

  async addOverlapValidationError(
    validationResult: ValidationResult,
    overlap: StartAndEnd,
    startDate: DateTime
  ): Promise<ValidationResult> {
    const message = await this.buildOverlapValidationMessage(overlap, startDate);

    validationResult.addError(
      message,
      ErrorType.Error,
      ActionType.OperationOverlap,
      overlap
    );

    return validationResult;
  }

  private async buildOverlapValidationMessage(
    overlap: StartAndEnd,
    startDate: DateTime
  ): Promise<string> {
    if (overlap.type === ActionType.DelayPauseOverlap) {
      return this.buildDelayPauseOverlapMessage(overlap, startDate);
    }

    return this.buildStandardOverlapMessage(overlap, startDate);
  }

  private async buildDelayPauseOverlapMessage(
    overlap: StartAndEnd,
    startDate: DateTime
  ): Promise<string> {
    const operationName = this.getRequiredOperationName(overlap.operationInstance);
    const previousOperationName = this.getRequiredOperationName(overlap.previousOperation);
    const affectedTimes = overlap.operationStartOnlyOverlapTime
      ? [overlap.operationStartOnlyOverlapTime.dateTime]
      : overlap.operationEndOnlyOverlapTime
        ? [overlap.operationEndOnlyOverlapTime.dateTime]
        : [overlap.stepInstance?.startTime?.dateTime, overlap.stepInstance?.endTime?.dateTime];
    const includeDate = this.showContractualDates(startDate, [
      overlap.previousOperation.startTime.dateTime, overlap.previousOperation.endTime.dateTime, ...affectedTimes
    ]);
    const delayRange = await this.getRequiredOperationRange(overlap.previousOperation, includeDate);

    if (overlap.operationStartOnlyOverlapTime) {
      const overlapTime = this.dateService.setFullTimeString(overlap.operationStartOnlyOverlapTime.dateTime, includeDate);

      return `There is an overlap with Operation:
      <strong>${operationName}</strong>, Start time: <strong>${overlapTime}</strong>
      and the Delay <strong>${previousOperationName} ${delayRange}</strong>`;
    }

    if (overlap.operationEndOnlyOverlapTime) {
      const overlapTime = this.dateService.setFullTimeString(overlap.operationEndOnlyOverlapTime.dateTime, includeDate);

      return `There is an overlap with Operation:
      <strong>${operationName}</strong>, End time: <strong>${overlapTime}</strong>
      and the Delay <strong>${previousOperationName} ${delayRange}</strong>`;
    }

    if (overlap.stepInstance) {
      const stepStart = this.dateService.setFullTimeString(overlap.stepInstance.startTime.dateTime, includeDate);
      const stepEnd = this.dateService.setFullTimeString(overlap.stepInstance.endTime.dateTime, includeDate);

      return `There is an overlap with Operation:
      <strong>${operationName}</strong>, step: <strong>${overlap.stepName} ${stepStart} to ${stepEnd}</strong>
      and the Delay <strong>${previousOperationName} ${delayRange}</strong>`;
    }

    throw new Error('DelayPauseOverlap did not contain operationStartOnlyOverlapTime, operationEndOnlyOverlapTime, or stepInstance.');
  }

  private getRequiredOperationName(operation: any): string {
    return operation.projectSpecificOperation.name
      ? operation.projectSpecificOperation.name
      : operation.projectSpecificOperation.operationSummary.name;
  }

  private async getRequiredOperationRange(operation: any, includeDate: boolean = true): Promise<string> {
    const start = this.dateService.setFullTimeString(operation.startTime.dateTime, includeDate);
    const end = this.dateService.setFullTimeString(operation.endTime.dateTime, includeDate);

    return `${start} to ${end}`;
  }

  async buildStandardOverlapMessage(
    overlap: StartAndEnd,
    startDate: DateTime,
    prefix: string = 'There is an overlap between',
    openEndedLabel?: string
  ): Promise<string> {
    const previousName = !overlap.previousOperation
      ? 'start of day'
      : this.getOperationName(overlap.previousOperation);

    const nextName = !overlap.operationInstance
      ? 'end of day'
      : this.getOperationName(overlap.operationInstance);

    const includeDate = this.showContractualDates(startDate, [
      overlap.previousOperation?.startTime?.dateTime, overlap.previousOperation?.endTime?.dateTime,
      overlap.operationInstance?.startTime?.dateTime, overlap.operationInstance?.endTime?.dateTime
    ]);
    const previousRange = await this.getPreviousOperationRange(overlap.previousOperation, startDate, includeDate, openEndedLabel);
    const nextRange = await this.getNextOperationRange(overlap.operationInstance, startDate, includeDate, openEndedLabel);

    return `${prefix} <strong>${previousName} ${previousRange}</strong>
      and <strong>${nextName} ${nextRange}</strong>`;
  }

  private getOperationName(operation: any, fallback = 'Unknown operation'): string {
    return (
      operation?.projectSpecificOperation?.name ||
      operation?.projectSpecificOperation?.operationSummary?.name ||
      fallback
    );
  }

  private async getStepRange(stepInstance: any): Promise<string> {
    const start = await this.formatFullTime(stepInstance?.startTime?.dateTime);
    const end = await this.formatFullTime(stepInstance?.endTime?.dateTime);

    return `${start} - ${end}`;
  }

  private async getDelayRange(previousOperation: any, startDate: DateTime): Promise<string> {
    if (!previousOperation) {
      return this.getEndOfDayDisplay(startDate);
    }

    return this.getOperationRange(previousOperation);
  }

  private async getPreviousOperationRange(previousOperation: any, startDate: DateTime, includeDate: boolean = true,
                                          openEndedLabel?: string): Promise<string> {
    if (!previousOperation) {
      return this.getStartOfDayDisplay(startDate, includeDate);
    }

    return this.getOperationRange(previousOperation, includeDate, openEndedLabel);
  }

  private async getNextOperationRange(operationInstance: any, startDate: DateTime, includeDate: boolean = true,
                                      openEndedLabel?: string): Promise<string> {
    if (!operationInstance) {
      return this.getEndOfDayDisplay(startDate, includeDate);
    }

    return this.getOperationRange(operationInstance, includeDate, openEndedLabel);
  }

  private async getOperationRange(operation: any, includeDate: boolean = true, openEndedLabel?: string): Promise<string> {
    const start = await this.formatFullTime(operation?.startTime?.dateTime, includeDate);

    if (!operation?.endTime?.dateTime) {
      return openEndedLabel ? `${start} to ${openEndedLabel}` : start;
    }

    const end = await this.formatFullTime(operation.endTime.dateTime, includeDate);
    return `${start} to ${end}`;
  }

  private async getStartOfDayDisplay(startDate: DateTime, includeDate: boolean = true): Promise<string> {
    return this.formatFullTime(startDate, includeDate);
  }

  private async getEndOfDayDisplay(startDate: DateTime, includeDate: boolean = true): Promise<string> {
    const endOfDay = await this.dateService.getEndOfDayLocally(startDate);
    return this.formatFullTime(endOfDay.dateTime.minus({minute: 1}), includeDate);
  }

  private async formatFullTime(dateTime: any, includeDate: boolean = true): Promise<string> {
    if (!dateTime) {
      return 'Unknown time';
    }

    return this.dateService.setFullTimeString(dateTime, includeDate);
  }

  private showContractualDates(reportingDay: DateTime, times: DateTime[]): boolean {
    // Each timestamp already carries its vessel-local offset. Comparing UTC days,
    // or converting everything to one offset, would misidentify reporting dates.
    return !reportingDay?.isValid || times.some(time => time != null
      && (!time.isValid || !this.dateService.compareDayOffsetsPreApplied(time, reportingDay)));
  }
}



// WEBPACK FOOTER //
// ./src/services/daily-log/daily-log-validation/daily-log-validation.service.ts